import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const CalculatorForm = ({ onCalculate, currentLanguage }) => {
  const [totalDebt, setTotalDebt] = useState('');
  const [monthlyPayment, setMonthlyPayment] = useState('');
  const [errors, setErrors] = useState({});

  // Format number with commas
  const formatNumber = (value) => {
    if (!value) return '';
    const number = value?.replace(/,/g, '');
    return number?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Parse formatted number to float
  const parseFormattedNumber = (value) => {
    return parseFloat(value?.replace(/,/g, '')) || 0;
  };

  const translations = {
    en: {
      totalDebtLabel: "Total Debt Amount",
      totalDebtPlaceholder: "Enter your total debt",
      monthlyPaymentLabel: "Monthly Payment",
      monthlyPaymentPlaceholder: "Enter your monthly payment",
      calculateButton: "Calculate Debt Freedom Date",
      errorRequired: "This field is required",
      errorPositive: "Amount must be greater than zero",
      errorPayment: "Monthly payment must be less than total debt"
    },
    es: {
      totalDebtLabel: "Monto Total de Deuda",
      totalDebtPlaceholder: "Ingrese su deuda total",
      monthlyPaymentLabel: "Pago Mensual",
      monthlyPaymentPlaceholder: "Ingrese su pago mensual",
      calculateButton: "Calcular Fecha de Libertad de Deuda",
      errorRequired: "Este campo es obligatorio",
      errorPositive: "El monto debe ser mayor que cero",
      errorPayment: "El pago mensual debe ser menor que la deuda total"
    },
    pt: {
      totalDebtLabel: "Valor Total da Dívida",
      totalDebtPlaceholder: "Digite sua dívida total",
      monthlyPaymentLabel: "Pagamento Mensal",
      monthlyPaymentPlaceholder: "Digite seu pagamento mensal",
      calculateButton: "Calcular Data de Liberdade da Dívida",
      errorRequired: "Este campo é obrigatório",
      errorPositive: "O valor deve ser maior que zero",
      errorPayment: "O pagamento mensal deve ser menor que a dívida total"
    },
    fr: {
      totalDebtLabel: "Montant Total de la Dette",
      totalDebtPlaceholder: "Entrez votre dette totale",
      monthlyPaymentLabel: "Paiement Mensuel",
      monthlyPaymentPlaceholder: "Entrez votre paiement mensuel",
      calculateButton: "Calculer la Date de Liberté de Dette",
      errorRequired: "Ce champ est obligatoire",
      errorPositive: "Le montant doit être supérieur à zéro",
      errorPayment: "Le paiement mensuel doit être inférieur à la dette totale"
    },
    de: {
      totalDebtLabel: "Gesamtschuldenbetrag",
      totalDebtPlaceholder: "Geben Sie Ihre Gesamtschuld ein",
      monthlyPaymentLabel: "Monatliche Zahlung",
      monthlyPaymentPlaceholder: "Geben Sie Ihre monatliche Zahlung ein",
      calculateButton: "Schuldenfreiheitsdatum Berechnen",
      errorRequired: "Dieses Feld ist erforderlich",
      errorPositive: "Der Betrag muss größer als Null sein",
      errorPayment: "Die monatliche Zahlung muss kleiner als die Gesamtschuld sein"
    },
    it: {
      totalDebtLabel: "Importo Totale del Debito",
      totalDebtPlaceholder: "Inserisci il tuo debito totale",
      monthlyPaymentLabel: "Pagamento Mensile",
      monthlyPaymentPlaceholder: "Inserisci il tuo pagamento mensile",
      calculateButton: "Calcola Data di Libertà dal Debito",
      errorRequired: "Questo campo è obbligatorio",
      errorPositive: "L\'importo deve essere maggiore di zero",
      errorPayment: "Il pagamento mensile deve essere inferiore al debito totale"
    },
    nl: {
      totalDebtLabel: "Totaal Schuldbedrag",
      totalDebtPlaceholder: "Voer uw totale schuld in",
      monthlyPaymentLabel: "Maandelijkse Betaling",
      monthlyPaymentPlaceholder: "Voer uw maandelijkse betaling in",
      calculateButton: "Bereken Schuldenvrije Datum",
      errorRequired: "Dit veld is verplicht",
      errorPositive: "Bedrag moet groter zijn dan nul",
      errorPayment: "Maandelijkse betaling moet minder zijn dan totale schuld"
    }
  };

  const t = translations?.[currentLanguage] || translations?.en;

  const validateForm = () => {
    const newErrors = {};

    const debtValue = parseFormattedNumber(totalDebt);
    const paymentValue = parseFormattedNumber(monthlyPayment);

    if (!totalDebt || totalDebt?.trim() === '') {
      newErrors.totalDebt = t?.errorRequired;
    } else if (debtValue <= 0) {
      newErrors.totalDebt = t?.errorPositive;
    }

    if (!monthlyPayment || monthlyPayment?.trim() === '') {
      newErrors.monthlyPayment = t?.errorRequired;
    } else if (paymentValue <= 0) {
      newErrors.monthlyPayment = t?.errorPositive;
    }

    if (totalDebt && monthlyPayment && paymentValue >= debtValue) {
      newErrors.monthlyPayment = t?.errorPayment;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onCalculate({
        totalDebt: parseFormattedNumber(totalDebt),
        monthlyPayment: parseFormattedNumber(monthlyPayment)
      });
    }
  };

  const handleTotalDebtChange = (e) => {
    const value = e?.target?.value?.replace(/[^0-9.]/g, '');
    setTotalDebt(formatNumber(value));
    if (errors?.totalDebt) {
      setErrors(prev => ({ ...prev, totalDebt: '' }));
    }
  };

  const handleMonthlyPaymentChange = (e) => {
    const value = e?.target?.value?.replace(/[^0-9.]/g, '');
    setMonthlyPayment(formatNumber(value));
    if (errors?.monthlyPayment) {
      setErrors(prev => ({ ...prev, monthlyPayment: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto">
      <div className="space-y-6 md:space-y-6">
        <Input
          type="text"
          inputMode="numeric"
          label={t?.totalDebtLabel}
          placeholder={t?.totalDebtPlaceholder}
          value={totalDebt}
          onChange={handleTotalDebtChange}
          error={errors?.totalDebt}
          required
          className="w-full"
        />

        <Input
          type="text"
          inputMode="numeric"
          label={t?.monthlyPaymentLabel}
          placeholder={t?.monthlyPaymentPlaceholder}
          value={monthlyPayment}
          onChange={handleMonthlyPaymentChange}
          error={errors?.monthlyPayment}
          required
          className="w-full"
        />

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          iconName="Calculator"
          iconPosition="left"
          className="h-12 md:h-10 text-base md:text-sm"
        >
          {t?.calculateButton}
        </Button>
      </div>
    </form>
  );
};

export default CalculatorForm;